import { ExpressionSpecification, SymbolLayerSpecification } from 'mapbox-gl';
import { Layer } from 'react-map-gl/mapbox';
import { COLOR_GREEN_400, COLOR_RED_300, COLOR_TEXT_PRIMARY_INVERSE, COLOR_YELLOW_300, FONT_SIZE_100 } from '@alltrails/denali/tokens';
import useMap from '../../hooks/useMap';
import { buildCollapsedPinFilters, buildCollapsedTrailIconImage, buildDynamicTrailPinProps } from '../../utils/buildPinIconExpressions';
import { collapsedTrailPinsActiveId, collapsedTrailPinsId, getLayerBeforeId } from '../../utils/layers';
import { dynamicPinMarkerVerticalOffset, trailLengthPinVerticalOffset } from '../../utils/svgDefinitions/svgMarkerComponents';
import {
  isTrailClosedExpression,
  isTrailCompletedExpression,
  isTrailPriorityHighExpression,
  isTrailPriorityLowExpression,
  isTrailPriorityMediumExpression,
  isTrailSavedExpression
} from '../../utils/trailPinConstants';

// For text-offset, we need to divide by the font size (FONT_SIZE_100) to get the correct value
const halfIconWidth = 8;
const horizontalTextOffsetWithIcon = (-1 * halfIconWidth) / FONT_SIZE_100;
const verticalTextOffset = (-1 * dynamicPinMarkerVerticalOffset) / FONT_SIZE_100;

type CollapsedTrailPinLayerProps = {
  sourceId: string;
  isHoveredExpression: ExpressionSpecification;
  isSelectedExpression: ExpressionSpecification;
  isViewedExpression: ExpressionSpecification;
  isActiveTrailExpression: ExpressionSpecification;
  commonLayoutProps: SymbolLayerSpecification['layout'];
};

const CollapsedTrailPinLayer = ({
  sourceId,
  isHoveredExpression,
  isSelectedExpression,
  isViewedExpression,
  isActiveTrailExpression,
  commonLayoutProps
}: CollapsedTrailPinLayerProps) => {
  const map = useMap();
  const { isCollapsedInactiveFilter, isCollapsedActiveFilter } = buildCollapsedPinFilters(
    isHoveredExpression,
    isSelectedExpression,
    isActiveTrailExpression
  );
  const collapsedTrailIconImage = buildCollapsedTrailIconImage(
    isHoveredExpression,
    isSelectedExpression,
    isViewedExpression,
    isActiveTrailExpression
  );
  const textFont = ['AllTrails Aeonik Medium', 'Manrope Bold'];
  const pinLayoutProps = buildDynamicTrailPinProps(isHoveredExpression, isSelectedExpression, isActiveTrailExpression);

  return (
    <>
      {/* Inactive collapsed pins (no text) - use icon-offset so small pill sits correctly */}
      <Layer
        source={sourceId}
        type="symbol"
        id={collapsedTrailPinsId}
        beforeId={getLayerBeforeId(map, collapsedTrailPinsId)}
        filter={isCollapsedInactiveFilter}
        layout={{
          ...commonLayoutProps,
          'icon-text-fit': 'none',
          'text-field': '',
          'text-font': textFont,
          'icon-offset': [0, trailLengthPinVerticalOffset],
          'icon-image': collapsedTrailIconImage
        }}
      />
      {/* Active/hovered collapsed pins - no icon-offset to avoid jump when expanding, text-offset is calculated to position the text correctly */}
      {/* text-offset is calculated to position the text correctly */}
      {/* rendered on top of other pins as per layers.ts */}
      <Layer
        source={sourceId}
        type="symbol"
        id={collapsedTrailPinsActiveId}
        beforeId={getLayerBeforeId(map, collapsedTrailPinsActiveId)}
        filter={isCollapsedActiveFilter}
        layout={{
          ...pinLayoutProps,
          'text-offset': [
            'case',
            ['any', isTrailCompletedExpression, isTrailSavedExpression, isTrailClosedExpression],
            [horizontalTextOffsetWithIcon, verticalTextOffset],
            [0, verticalTextOffset]
          ],
          'icon-image': collapsedTrailIconImage
        }}
        paint={{
          'text-color': [
            'case',
            isTrailPriorityHighExpression,
            COLOR_RED_300,
            isTrailPriorityMediumExpression,
            COLOR_YELLOW_300,
            isTrailPriorityLowExpression,
            COLOR_GREEN_400,
            COLOR_TEXT_PRIMARY_INVERSE
          ]
        }}
      />
    </>
  );
};

export default CollapsedTrailPinLayer;
